import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from 'react';
import { selectCurrentEscritorio } from '@/features/escritorio';
import { derivarParametrosDoAgente, fetchNegociacaoIndividualAgents, selectWizardAgents, selectWizardAgentsError, selectWizardAgentsLoading, selectWizardAgentsPagination } from '@/features/negociacaoIndividualWizard';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { alterarDadoDaSimulacao, buscarSimulacaoAtual, iniciarSimulacao, trocarAgenteDaSimulacao } from '../store/actions';
import { buscarAgenteDaSimulacao } from '../store/agente/actions';
import { camposVisiveisDoInicio, errosDosCamposDoInicio, obrigatoriosPreenchidos as camposObrigatoriosPreenchidos, valoresPadraoDoAgente } from '../utils/camposDeInicio';
import { PRIMEIRA_PAGINA_DE_AGENTES } from '../utils/paginaDeAgentes';
import { selectAgenteDaSimulacao, selectFormularioDaSimulacao, selectInicioDaSimulacao, selectSimulacaoAtual, selectErroDoEncerramento } from '../store/selectors';
import { valoresPadraoAplicados } from '../store/slice';
import type { SituacaoDosCamposDeInicio } from '../types';
const situacaoDosCampos = (flowId: string | null, carregando: boolean, falhou: boolean, quantidadeDeCampos: number): SituacaoDosCamposDeInicio => {
  if (!flowId) return 'sem-agente';
  if (carregando) return 'carregando';
  if (falhou) return 'erro';
  return quantidadeDeCampos === 0 ? 'sem-campos' : 'campos';
};

/**
 * Agente, dados de início e Iniciar (CA-3, CA-4). A lista de agentes e a derivação dos campos são
 * compartilhadas com o wizard individual. A seleção e a definição carregada ficam no slice da
 * simulação: testar outro agente não pode alterar uma negociação individual em andamento.
 */
export function useConfiguracaoDaSimulacao() {
  const $ = _c(76);
  const dispatch = useAppDispatch();
  const escritorioId = useAppSelector(selectCurrentEscritorio)?.id ?? null;
  const agents = useAppSelector(selectWizardAgents);
  const agentsLoading = useAppSelector(selectWizardAgentsLoading);
  const agentsPagination = useAppSelector(selectWizardAgentsPagination);
  const erroDaLista = useAppSelector(selectWizardAgentsError);
  const {
    dados: selectedAgent,
    carregando: agentDetailsLoading,
    erro: erroDoAgente
  } = useAppSelector(selectAgenteDaSimulacao);
  const formulario = useAppSelector(selectFormularioDaSimulacao);
  const simulacao = useAppSelector(selectSimulacaoAtual);
  const erroDoEncerramento = useAppSelector(selectErroDoEncerramento);
  const {
    iniciando,
    erro: erroDoInicio
  } = useAppSelector(selectInicioDaSimulacao);
  let t0;
  if ($[0] !== selectedAgent) {
    t0 = derivarParametrosDoAgente(selectedAgent);
    $[0] = selectedAgent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const agentVariables = t0;
  const agentsError = erroDoAgente ?? erroDaLista;
  const flowId = formulario.flowId;
  const agenteCarregado = flowId !== null && selectedAgent?.id === flowId && !agentDetailsLoading && !agentsError;
  let t1;
  if ($[2] !== dispatch) {
    t1 = () => {
      dispatch(fetchNegociacaoIndividualAgents(PRIMEIRA_PAGINA_DE_AGENTES));
      dispatch(buscarSimulacaoAtual());
    };
    $[2] = dispatch;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== dispatch || $[5] !== escritorioId) {
    t2 = [dispatch, escritorioId];
    $[4] = dispatch;
    $[5] = escritorioId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== dispatch || $[8] !== flowId) {
    t3 = () => {
      if (flowId) {
        dispatch(buscarAgenteDaSimulacao(flowId));
      }
    };
    $[7] = dispatch;
    $[8] = flowId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== dispatch || $[11] !== escritorioId || $[12] !== flowId) {
    t4 = [dispatch, flowId, escritorioId];
    $[10] = dispatch;
    $[11] = escritorioId;
    $[12] = flowId;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[14] !== agentVariables || $[15] !== agenteCarregado || $[16] !== dispatch || $[17] !== flowId) {
    t5 = () => {
      if (!agenteCarregado || !flowId) {
        return;
      }
      dispatch(valoresPadraoAplicados({
        flowId,
        valores: valoresPadraoDoAgente(agentVariables)
      }));
    };
    t6 = [agenteCarregado, agentVariables, dispatch, flowId];
    $[14] = agentVariables;
    $[15] = agenteCarregado;
    $[16] = dispatch;
    $[17] = flowId;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useEffect(t5, t6);
  let situacao;
  let t7;
  let variables;
  if ($[20] !== agentDetailsLoading || $[21] !== agentVariables || $[22] !== agenteCarregado || $[23] !== agentsError || $[24] !== flowId || $[25] !== formulario.valores) {
    variables = camposVisiveisDoInicio(agentVariables);
    situacao = situacaoDosCampos(flowId, agentDetailsLoading, Boolean(agentsError) || !agenteCarregado, variables.length);
    t7 = camposObrigatoriosPreenchidos(variables, formulario.valores);
    $[20] = agentDetailsLoading;
    $[21] = agentVariables;
    $[22] = agenteCarregado;
    $[23] = agentsError;
    $[24] = flowId;
    $[25] = formulario.valores;
    $[26] = situacao;
    $[27] = t7;
    $[28] = variables;
  } else {
    situacao = $[26];
    t7 = $[27];
    variables = $[28];
  }
  const obrigatoriosPreenchidos = t7;
  let t8;
  if ($[29] !== erroDoInicio) {
    t8 = errosDosCamposDoInicio(erroDoInicio);
    $[29] = erroDoInicio;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const errors = t8;
  let t9;
  if ($[31] !== agentDetailsLoading || $[32] !== dispatch || $[33] !== flowId) {
    t9 = id => {
      if (id === (flowId ?? "") || agentDetailsLoading) {
        return;
      }
      dispatch(trocarAgenteDaSimulacao(id || null));
    };
    $[31] = agentDetailsLoading;
    $[32] = dispatch;
    $[33] = flowId;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const selecionarAgente = t9;
  let t10;
  if ($[35] !== dispatch) {
    t10 = (nome, valor) => {
      dispatch(alterarDadoDaSimulacao(nome, valor));
    };
    $[35] = dispatch;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const alterarDado = t10;
  let t11;
  if ($[37] !== dispatch || $[38] !== simulacao) {
    t11 = () => {
      dispatch(iniciarSimulacao({
        motivo: simulacao ? "REINICIO" : "INICIO"
      }));
    };
    $[37] = dispatch;
    $[38] = simulacao;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const iniciar = t11;
  let t12;
  if ($[40] !== dispatch || $[41] !== flowId) {
    t12 = () => {
      if (flowId) {
        dispatch(buscarAgenteDaSimulacao(flowId));
      }
    };
    $[40] = dispatch;
    $[41] = flowId;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const recarregarAgente = t12;
  const t13 = simulacao?.agenteNome || (agenteCarregado ? selectedAgent?.name ?? "Lexi" : "Lexi");
  const t14 = simulacao !== null;
  const t15 = agenteCarregado && obrigatoriosPreenchidos && !iniciando;
  let t16;
  if ($[43] !== dispatch) {
    t16 = () => dispatch(fetchNegociacaoIndividualAgents(PRIMEIRA_PAGINA_DE_AGENTES));
    $[43] = dispatch;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== agentsLoading || $[46] !== agentsPagination.hasMore || $[47] !== agentsPagination.limit || $[48] !== agentsPagination.page || $[49] !== agentsPagination.search || $[50] !== dispatch) {
    t17 = () => {
      if (agentsLoading || !agentsPagination.hasMore) {
        return;
      }
      dispatch(fetchNegociacaoIndividualAgents({
        page: agentsPagination.page + 1,
        limit: agentsPagination.limit,
        search: agentsPagination.search
      }));
    };
    $[45] = agentsLoading;
    $[46] = agentsPagination.hasMore;
    $[47] = agentsPagination.limit;
    $[48] = agentsPagination.page;
    $[49] = agentsPagination.search;
    $[50] = dispatch;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== agentDetailsLoading || $[53] !== agenteCarregado || $[54] !== agents || $[55] !== agentsError || $[56] !== agentsLoading || $[57] !== agentsPagination.hasMore || $[58] !== alterarDado || $[59] !== erroDoEncerramento || $[60] !== erroDoInicio || $[61] !== errors || $[62] !== flowId || $[63] !== formulario.valores || $[64] !== iniciando || $[65] !== iniciar || $[66] !== recarregarAgente || $[67] !== selecionarAgente || $[68] !== situacao || $[69] !== t13 || $[70] !== t14 || $[71] !== t15 || $[72] !== t16 || $[73] !== t17 || $[74] !== variables) {
    t18 = {
      agents,
      agentsLoading,
      agentsError,
      agentDetailsLoading,
      agenteCarregado,
      agentName: t13,
      selectedId: flowId,
      variables,
      situacaoDosCampos: situacao,
      valores: formulario.valores,
      errors,
      iniciando,
      erroDoInicio,
      erroDoEncerramento,
      started: t14,
      podeIniciar: t15,
      selecionarAgente,
      alterarDado,
      iniciar,
      recarregarAgentes: t16,
      haMaisAgentes: agentsPagination.hasMore,
      carregarMaisAgentes: t17,
      recarregarAgente
    };
    $[52] = agentDetailsLoading;
    $[53] = agenteCarregado;
    $[54] = agents;
    $[55] = agentsError;
    $[56] = agentsLoading;
    $[57] = agentsPagination.hasMore;
    $[58] = alterarDado;
    $[59] = erroDoEncerramento;
    $[60] = erroDoInicio;
    $[61] = errors;
    $[62] = flowId;
    $[63] = formulario.valores;
    $[64] = iniciando;
    $[65] = iniciar;
    $[66] = recarregarAgente;
    $[67] = selecionarAgente;
    $[68] = situacao;
    $[69] = t13;
    $[70] = t14;
    $[71] = t15;
    $[72] = t16;
    $[73] = t17;
    $[74] = variables;
    $[75] = t18;
  } else {
    t18 = $[75];
  }
  return t18;
}